/**
 * After a deploy, a browser that still has the previous version open asks for JS/CSS chunks
 * that no longer exist on the server. Reloading the page fetches the new index.html and with
 * it the current chunks, so a failed chunk load reloads the page instead of breaking it.
 *
 * The decision is made by public/versionReload.js, which is loaded before the bundle so that it
 * also covers a main bundle that cannot be used. It allows one reload per five minutes and none
 * without contact with the server, so offline mode keeps the page.
 */
declare global {
  interface Window {
    diremoReloadForNewVersion?: (url?: string) => Promise<boolean>;
  }
}

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' || (error as { code?: string }).code === 'CSS_CHUNK_LOAD_FAILED');

/**
 * Wraps a dynamic import, e.g. `lazy(reloadOnChunkError(() => import('./Page')))`.
 */
export const reloadOnChunkError =
  <T>(factory: () => Promise<T>) =>
  (): Promise<T> =>
    factory().catch(async (error: unknown) => {
      if (!isChunkLoadError(error) || !window.diremoReloadForNewVersion) {
        throw error;
      }
      // webpack and mini-css-extract-plugin put the address of the failed file on the error.
      const url = (error as { request?: string }).request;
      if (!(await window.diremoReloadForNewVersion(url))) {
        throw error;
      }
      // Never settles: the page is going away, and rendering an error until then would flash.
      return new Promise<T>(() => undefined);
    });
